import { FaWhatsapp } from "react-icons/fa6";
import { FaPhoneAlt } from "react-icons/fa";
import styles from "./seller-contact-dialog.module.css";
import { MdChat, MdClose } from "react-icons/md";
import {
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "@/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { CircleFlag } from "react-circle-flags";
import { IoIosArrowDown } from "react-icons/io";
import {
  getCountryFromPrefix,
  phonePrefixes,
} from "@/lib/phone-prefixes/prefixes";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { UserTypes } from "@/types/types";
import { generalContactHandler } from "./handlers";
import { generalContactSchema, generalContactSchemaAuth } from "./schemas";
import { toast } from "react-toastify";

export default function GeneralContactDialog({
  userSub,
  photoUrl,
  userType,
  userName,
  closeCallback,
  phoneNumber,
  hasWhatsAppActive,
  dialogRef,
  isComponent = false,
}: {
  userSub: string;
  photoUrl: string;
  userType: UserTypes;
  userName: string;
  closeCallback: () => void;
  phoneNumber: string | null;
  hasWhatsAppActive: boolean | null;
  dialogRef: RefObject<HTMLDivElement | null>;
  isComponent?: boolean;
}) {
  const t = useTranslations("ContactDialog.general");
  const [isPhoneNumberActive, setIsPhoneNumberActive] = useState(false);
  const [isPhonePrefixOpen, setIsPhonePrefixOpen] = useState(false);
  const locale = useLocale();
  const [numberSearchQuery, setNumberSearchQuery] = useState("");
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });
  const filteredPrefixes = phonePrefixes.filter((country) =>
    (displayNames.of(country.code) ?? "")
      .toLowerCase()
      .includes(numberSearchQuery.toLowerCase()),
  );
  const firstName = userName.split(" ")[0] ?? "";
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [activePrefix, setActivePrefix] = useState("");
  const [message, setMessage] = useState(
    t("placeholderMessage", { userName: firstName }),
  );
  const { userData } = useUserAuthDataContext();

  return (
    <div
      className={`${styles.dialogWrapper} ${
        isComponent ? styles.isComponent : ""
      }`}
      onClick={(e) => {
        if (!isComponent) {
          e.stopPropagation();
          closeCallback();
        }
      }}
    >
      <div
        className={styles.dialogContent}
        ref={dialogRef}
        onClick={(e) => !isComponent && e.stopPropagation()}
      >
        <div className={styles.closeButtonWrapper}>
          <span className={styles.title}>{t("titleMessage")}</span>
          <button
            className={styles.closeButton}
            onClick={(e) => {
              e.preventDefault();
              closeCallback();
            }}
          >
            <MdClose className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.agentDetails}>
          <Link
            className={`${styles.imageLink} ${styles.imageLinkDesktop}`}
            target="_blank"
            rel="noopener noreferrer"
            href={makeLocalizedHrefNoLocale(locale, "profiles", [`${userSub}`])}
          >
            <Image
              className={styles.agentImage}
              src={photoUrl}
              width={128}
              height={128}
              alt="User uploaded profile photo"
            />
          </Link>
          <p className={styles.userName}>{userName}</p>
        </div>
        {!userData.idToken ? (
          <div className={styles.mainContentWrapper}>
            <span className={styles.description}>{t("contactInfo")}</span>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const validation = generalContactSchema.safeParse({
                  fullName,
                  email,
                  phoneNumber: `${activePrefix + phoneNumberInput}`,
                  message,
                });
                if (!validation.success) {
                  const firstError = validation.error.errors[0]?.message;
                  toast(t(firstError), { type: "error" });
                  return;
                }
                const isSuccess = await generalContactHandler(
                  userSub,
                  userType,
                  fullName,
                  email,
                  `${activePrefix + phoneNumberInput}`,
                  message,
                  userData.idToken,
                  userData.sub,
                );
                if (isSuccess) {
                  setActivePrefix("");
                  setEmail("");
                  setFullName("");
                  setMessage("");
                  setPhoneNumberInput("");
                  closeCallback();
                }
              }}
              className={styles.contactForm}
            >
              <div className={`${styles.fieldWrapper}`}>
                <input
                  type="text"
                  placeholder={t("completeName")}
                  value={fullName}
                  onChange={(e) => {
                    e.preventDefault();
                    setFullName(e.target.value);
                  }}
                />
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <input
                  type="email"
                  placeholder={t("email")}
                  value={email}
                  onChange={(e) => {
                    e.preventDefault();
                    setEmail(e.target.value);
                  }}
                />
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <div className={styles.phoneNumberWrapper}>
                  <button
                    className={styles.prefixButton}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPhonePrefixOpen(!isPhonePrefixOpen);
                      setNumberSearchQuery("");
                    }}
                  >
                    {activePrefix && (
                      <span>
                        <CircleFlag
                          countryCode={getCountryFromPrefix(
                            activePrefix,
                          ).toLocaleLowerCase()}
                          className={styles.countryIcon}
                        />
                      </span>
                    )}
                    {activePrefix == "" ? t("none") : activePrefix}
                    <span>
                      <IoIosArrowDown className={styles.arrowIcon} />
                    </span>
                  </button>
                  <input
                    className={styles.phoneInput}
                    type="number"
                    placeholder="797 285 954"
                    value={phoneNumberInput}
                    onChange={(e) => {
                      e.preventDefault();
                      setPhoneNumberInput(e.target.value);
                    }}
                  />
                  {isPhonePrefixOpen && (
                    <div className={styles.phonePrefixesWrapper}>
                      <input
                        className={styles.phonePrefixInput}
                        type="text"
                        placeholder={t("phoneCountryPlaceholder")}
                        onChange={(e) => {
                          e.preventDefault();
                          setNumberSearchQuery(e.target.value);
                        }}
                      />
                      <ul>
                        {filteredPrefixes.map((activePrefix, index) => {
                          return (
                            <li key={index}>
                              <button
                                className={styles.prefixListButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setActivePrefix(activePrefix.dial_code);
                                  setIsPhonePrefixOpen(false);
                                  setNumberSearchQuery("");
                                }}
                              >
                                <span>
                                  <CircleFlag
                                    countryCode={activePrefix.code.toLowerCase()}
                                    className={styles.countryIcon}
                                  />
                                </span>
                                <span>{activePrefix.dial_code}</span>
                                <span className={styles.phoneCountryText}>
                                  {displayNames.of(activePrefix.code) ?? ""}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className={`${styles.fieldWrapper}`}>
                <textarea
                  className={styles.messageTextArea}
                  placeholder={t("enterMessage")}
                  value={message}
                  maxLength={400}
                  onChange={(e) => {
                    e.preventDefault();
                    setMessage(e.target.value);
                  }}
                />
              </div>
              <button type="submit" className={styles.sendButton}>
                {t("sendMessage")}
              </button>
            </form>
          </div>
        ) : (
          <div className={styles.mainContentWrapper}>
            <form
              className={styles.contactForm}
              onSubmit={async (e) => {
                e.preventDefault();
                const validation = generalContactSchemaAuth.safeParse({
                  message,
                });
                if (!validation.success) {
                  const firstError = validation.error.errors[0]?.message;
                  toast(t(firstError), { type: "error" });
                  return;
                }
                const isSuccess = await generalContactHandler(
                  userSub,
                  userType,
                  "",
                  "",
                  "",
                  message,
                  userData.idToken,
                  userData.sub,
                );

                if (isSuccess) {
                  setActivePrefix("");
                  setEmail("");
                  setFullName("");
                  setMessage("");
                  setPhoneNumberInput("");
                  closeCallback();
                }
              }}
            >
              <div className={styles.fieldWrapper}>
                <textarea
                  className={styles.messageTextArea}
                  placeholder={t("enterMessage")}
                  value={message}
                  maxLength={400}
                  onChange={(e) => {
                    e.preventDefault();
                    setMessage(e.target.value);
                  }}
                  minLength={20}
                />
              </div>
              <button type="submit" className={styles.sendButton}>
                {t("sendMessage")}
              </button>
            </form>
          </div>
        )}
        {phoneNumber !== null && <span className={styles.or}>{t("or")}</span>}
        {phoneNumber !== null && (
          <div className={styles.buttonsWrapper}>
            {!isPhoneNumberActive && (
              <button
                className={styles.button}
                onClick={(e) => {
                  e.preventDefault();
                  setIsPhoneNumberActive(true);
                }}
              >
                <span>
                  <FaPhoneAlt
                    className={`${styles.buttonIcon} ${styles.phoneIcon}`}
                  />
                </span>
                {t("showNumber")}
              </button>
            )}
            {phoneNumber !== null && isPhoneNumberActive && (
              <button
                className={styles.button}
                onClick={async (e) => {
                  e.preventDefault();

                  // 1. Ensure the value is a string, otherwise writeText crashes
                  const textToCopy = phoneNumber ? String(phoneNumber) : "";

                  if (!textToCopy) {
                    toast(t("copyFailed"), { type: "error" });
                    return;
                  }

                  // 2. Try the modern Clipboard API first (Requires HTTPS or Localhost)
                  if (navigator.clipboard && window.isSecureContext) {
                    try {
                      await navigator.clipboard.writeText(textToCopy);
                      toast(t("phoneCopied"), { type: "success" });
                      return; // Exit if successful
                    } catch (error) {
                      console.warn(
                        "Modern clipboard API failed, trying fallback...",
                        error,
                      );
                    }
                  }

                  // 3. The Bulletproof Fallback (Works on HTTP, older devices, and iOS WebViews)
                  try {
                    const textArea = document.createElement("textarea");
                    textArea.value = textToCopy;

                    // Hide the textarea entirely off-screen
                    textArea.style.position = "absolute";
                    textArea.style.left = "-999999px";

                    document.body.appendChild(textArea);

                    // Special selection logic to make iOS Safari happy
                    if (navigator.userAgent.match(/ipad|iphone/i)) {
                      const range = document.createRange();
                      range.selectNodeContents(textArea);
                      const selection = window.getSelection();
                      selection?.removeAllRanges();
                      selection?.addRange(range);
                      textArea.setSelectionRange(0, 999999);
                    } else {
                      textArea.select();
                    }

                    document.execCommand("copy");
                    textArea.remove();

                    toast(t("phoneCopied"), { type: "success" });
                  } catch (fallbackError) {
                    console.error(
                      "Fallback clipboard also failed:",
                      fallbackError,
                    );
                    toast(t("copyFailed"), { type: "error" });
                  }
                }}
                aria-label={t("copyPhoneNumber")}
              >
                <span className={styles.phoneText}>
                  <FaPhoneAlt
                    className={`${styles.buttonIcon} ${styles.phoneIcon}`}
                  />
                  <span>&lrm;{phoneNumber}</span>
                </span>
              </button>
            )}
            {hasWhatsAppActive && (
              <a
                className={`${styles.button} ${styles.whatsAppButton}`}
                target="_blank"
                rel="noopener noreferrer"
                href={`https://wa.me/${formatPhoneForWhatsApp(phoneNumber)}`}
              >
                <span>
                  <FaWhatsapp className={styles.buttonIcon} />
                </span>
                {t("whatsApp")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatPhoneForWhatsApp(phone: string): string {
  return phone.replace(/[^0-9]/g, ""); // Keep only digits
}
